/**
 * Cteamcard Component
 * Card for displaying team information
 */
"use client";
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactElement } from "react";
import { memo, useMemo } from "react";
import { motion, type MotionValue } from "framer-motion";
import { Ccontainer } from "../Ccontainer";
import { Cbutton } from "../Cbutton";
import { ButtonPreset, ContainerPreset } from "@/constants/componentPresets";
import { TeamCategory } from "@cabal/shared/domain/session/team/enums.js";
import { useViewport } from "@/hooks/dom";
import { useViewportMetrics } from "@/hooks/dom";
import { Viewport } from "@/theme/breakpoints";
import { useModels } from "@/contexts/models";
import { dataEl, type DataElScope } from "@/utils/dataEl";
import { CteamcardCatchPhrase } from "./CteamcardCatchPhrase";
import { CteamcardFooter } from "./CteamcardFooter";
import { CteamcardTitleRow } from "./CteamcardTitleRow";
import { getCteamcardBackgroundStyle, getCteamcardContentMotionStyle, getCteamcardContentSurface, getCteamcardMetrics, } from "./Cteamcard.styles";
export interface TeamData {
    id: string;
    name: string;
    memberCount: number;
    /** Model identifiers (numeric IDs as strings) to look up providers */
    models: string[];
    catchPhrase?: string; // Team motto/tagline
    category?: TeamCategory; // Team category for icon
}
export interface CteamcardProps {
    dataElScope: DataElScope;
    teamData: TeamData;
    isSelected?: boolean;
    isLoading?: boolean;
    /** Cbutton delivers the click event — callers may read the card element for morph origins. */
    onClick?: (event?: ReactMouseEvent) => void;
    className?: string;
    style?: CSSProperties;
    fixedWidth?: number | string;
    fixedHeight?: number | string;
    contentOpacity?: number | MotionValue<number>;
    useBackdropFilter?: boolean;
    cardContainerPreset?: ContainerPreset;
    priority?: boolean;
    /** Overrides the card surface background (default: glassy gradient). */
    cardBackground?: string;
    /** Overrides the inner content container's backdrop blur (default: from preset). */
    contentBackdropBlur?: number;
    /** Optional pre-resolved vendor slugs for the card's models. When provided the
     *  card skips its own `useModels().getUniqueVendors(...)` rebuild — useful for
     *  callers that render many cards from a single team list. */
    precomputedVendors?: string[];
}
export const Cteamcard = memo(function Cteamcard({ dataElScope, teamData, isLoading = false, onClick, className, style, fixedWidth, fixedHeight, contentOpacity, useBackdropFilter = true, cardContainerPreset, priority = false, cardBackground, contentBackdropBlur, precomputedVendors, }: CteamcardProps): ReactElement {
    const { viewport } = useViewport();
    const { vw } = useViewportMetrics();
    const isMobile = viewport === Viewport.Mobile;
    const { getUniqueVendors } = useModels();
    const { cardWidth, cardHeight, contentGap } = getCteamcardMetrics({
        fixedWidth,
        fixedHeight,
        isMobile,
        vw,
    });
    const cardBackgroundStyle = getCteamcardBackgroundStyle({ cardBackground });
    const contentSurface = getCteamcardContentSurface({ cardBackground });
    const teamDataModels = teamData.models;
    const llmVendors = useMemo(() => {
        return precomputedVendors ?? getUniqueVendors(teamDataModels ?? []);
    }, [precomputedVendors, teamDataModels, getUniqueVendors]);
    return (
    <Cbutton rootAttributes={{ "data-el": dataEl(dataElScope, "Surface/Root") }} preset={ButtonPreset.Secondary} containerPreset={cardContainerPreset} backdropBlur={0} padding={0} height="100%" radius={48} onClick={onClick} isLoading={isLoading} className={className} containerStyle={cardBackgroundStyle} style={{
            width: cardWidth,
            height: cardHeight,
            ...style,
        }}>
      <Ccontainer preset={cardContainerPreset} radius={48} flexDirection="column" justifyContent="space-between" alignItems="stretch" gap={contentGap} width="100%" height="100%" padding="25px" backdropBlur={contentBackdropBlur} backgroundColor={contentSurface.backgroundColor} backgroundOpacity={contentSurface.backgroundOpacity}>
        <motion.div style={getCteamcardContentMotionStyle({ contentGap, contentOpacity })}>
          <CteamcardTitleRow dataElScope={dataElScope} category={teamData.category} isMobile={isMobile} priority={priority} teamName={teamData.name}/>
          <CteamcardCatchPhrase catchPhrase={teamData.catchPhrase} isMobile={isMobile}/>
          <CteamcardFooter isMobile={isMobile} llmVendors={llmVendors} memberCount={teamData.memberCount} priority={priority} useBackdropFilter={useBackdropFilter}/>
        </motion.div>
      </Ccontainer>
    </Cbutton>
    );
});
